import React from "react"
import styled from "styled-components"
import { Parallax, Background } from "react-parallax"
import useBreakpoint from "../../../hooks/use-breakpoint"

import Box from "../Box"
import StandardPadding from "../StandardPadding"

const StyledParallax = styled(Parallax)`
  .react-parallax-background-children {
    min-width: 100%;
  }
  .react-parallax-content {
    min-height: ${props => {
      return props.fullHeight ? "100vh" : "100%"
    }};
    display: flex;
    align-items: ${props => {
      return props.contentLocation
    }};
    background-color: ${props => props.parallaxHex};
  }
  .react-parallax-bgimage {
    z-index: 10;
  }
  img {
    ${props =>
      props.notGatsby && props.strength >= -190
        ? `height: 120% !important`
        : null}
  }
`

const StyledWrapper = styled(Box)`
  position: relative;
  z-index: 0;
  display: block;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-image: ${props => `url(${props.bgImage})`};
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: cover;
  background-attachment: fixed;
`

const StyledOverlay = styled(Box)`
  z-index: 10;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => props.parallaxOpacity};
  background-color: ${props => props.parallaxHex};
`

const StyledContent = styled(Box)`
  display: flex;
  align-items: ${props => {
    return props.contentLocation
  }};
  z-index: 10;
  min-height: ${props => {
    return props.fullHeight ? "100vh" : "100%"
  }};
`

export const RenderParallaxSection = ({
  notGatsby,
  noStandardPadding,
  parallaxOptions,
  imageOrVideo,
  strength,
  bgImageStyle,
  parallaxOpacity,
  parallaxHex,
  parent,
  standardPaddingProps,
  isFullHeight,
  contentLocation,
  children,
}) => {
  const isVideoSelected = imageOrVideo?.props?.isVideoSelected
  const hasMobileImg = !imageOrVideo?.props?.mobileImage ? false : true
  const isMobileBp = useBreakpoint("md")
  const imageUrl =
    isMobileBp && hasMobileImg
      ? imageOrVideo?.props?.mobileImage?.url
      : imageOrVideo?.props?.desktopImage?.url

  const camelize = str => {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
      if (+match === 0) return ""
      return index === 0 ? match.toLowerCase() : match.toUpperCase()
    })
  }

  const mergeOptions = (parallax, noStandardPaddingField) => {
    let padding = noStandardPaddingField
      ? "noStandardPadding"
      : "withStandardPadding"
    let concatenateOptions = parallax + " " + padding
    return camelize(concatenateOptions)
  }

  const optionForSwitch = mergeOptions(parallaxOptions, noStandardPadding)

  const SwitchParallax = option => {
    switch (option) {
      case "scrollWithStandardPadding":
        return (
          <StyledParallax
            fullHeight={isFullHeight}
            bgImage={!isVideoSelected ? imageUrl : null}
            notGatsby={notGatsby}
            strength={strength}
            bgImageStyle={bgImageStyle}
            parent={parent}
            contentLocation={contentLocation}
            parallaxHex={parallaxHex}
          >
            {isVideoSelected && <Background>{imageOrVideo}</Background>}
            <StyledOverlay
              parallaxOpacity={parallaxOpacity}
              parallaxHex={parallaxHex}
            />
            <StandardPadding {...standardPaddingProps}>
              {children}
            </StandardPadding>
          </StyledParallax>
        )
      case "scrollNoStandardPadding":
        return (
          <StyledParallax
            fullHeight={isFullHeight}
            bgImage={!isVideoSelected ? imageUrl : null}
            notGatsby={notGatsby}
            strength={strength}
            bgImageStyle={bgImageStyle}
            parent={parent}
            contentLocation={contentLocation}
            parallaxHex={parallaxHex}
          >
            {isVideoSelected && <Background>{imageOrVideo}</Background>}
            <StyledOverlay
              parallaxOpacity={parallaxOpacity}
              parallaxHex={parallaxHex}
            />
            {children}
          </StyledParallax>
        )
      case "fixedWithStandardPadding":
        return (
          <StyledWrapper bgImage={!isVideoSelected && imageUrl}>
            <StyledOverlay
              parallaxOpacity={parallaxOpacity}
              parallaxHex={parallaxHex}
            />
            <StyledContent
              contentLocation={contentLocation}
              fullHeight={isFullHeight}
            >
              <StandardPadding {...standardPaddingProps}>
                {children}
              </StandardPadding>
            </StyledContent>
          </StyledWrapper>
        )
      case "fixedNoStandardPadding":
        return (
          <StyledWrapper bgImage={!isVideoSelected && imageUrl}>
            <StyledOverlay
              parallaxOpacity={parallaxOpacity}
              parallaxHex={parallaxHex}
            />
            <StyledContent
              contentLocation={contentLocation}
              fullHeight={isFullHeight}
            >
              {children}
            </StyledContent>
          </StyledWrapper>
        )
    }
  }

  return <>{SwitchParallax(optionForSwitch)}</>
}
